import { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Package, ArrowUpRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { PARTNER_BRANDS, partnerStrings } from "../lib/partnersData";
import { QuoteModal } from "./QuoteModal";

// Build a flat searchable index from all brand catalogs.
const buildIndex = () => {
  const index = [];
  PARTNER_BRANDS.forEach((brand) => {
    const shortName = brand.name.split(/\s+/)[0];
    brand.groups.forEach((group) => {
      group.products.forEach((p) => {
        index.push({
          id: `${brand.id}-${group.id}-${p.slug}`,
          product: p,
          brandId: brand.id,
          brandShort: shortName,
          brandColor: brand.color,
          imageFolder: brand.imageFolder,
          theme: brand.theme,
          groupTitle: group.title,
          groupId: group.id,
        });
      });
    });
  });
  return index;
};

const STRINGS = {
  pt: {
    placeholder: "Pesquisar equipamento… ex: SLR 5500, câmara térmica, PABX",
    results: (n) => `${n} ${n === 1 ? "resultado" : "resultados"}`,
    empty: "Sem resultados. Tente outro termo.",
    hint: "Experimente: ",
    examples: ["câmara", "roteador", "MOTOTRBO", "DECT"],
    title: "Encontre o seu equipamento",
    subtitle:
      "Pesquise em todo o nosso catálogo de parceiros e peça orçamento num clique.",
  },
  en: {
    placeholder: "Search equipment… e.g. SLR 5500, thermal camera, PBX",
    results: (n) => `${n} ${n === 1 ? "result" : "results"}`,
    empty: "No results. Try another term.",
    hint: "Try: ",
    examples: ["camera", "router", "MOTOTRBO", "DECT"],
    title: "Find your equipment",
    subtitle:
      "Search across our full partner catalog and request a quote in one click.",
  },
  es: {
    placeholder: "Buscar equipo… p. ej. SLR 5500, cámara térmica, PBX",
    results: (n) => `${n} ${n === 1 ? "resultado" : "resultados"}`,
    empty: "Sin resultados. Prueba otro término.",
    hint: "Prueba: ",
    examples: ["cámara", "router", "MOTOTRBO", "DECT"],
    title: "Encuentra tu equipo",
    subtitle:
      "Busca en todo nuestro catálogo de socios y solicita presupuesto en un clic.",
  },
};

const normalize = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const ProductSearch = () => {
  const { lang } = useLang();
  const navigate = useNavigate();
  const s = STRINGS[lang] || STRINGS.pt;
  const psCta =
    partnerStrings[lang]?.card_quote_cta || partnerStrings.pt.card_quote_cta;

  const [query, setQuery] = useState("");
  const [openResults, setOpenResults] = useState(false);
  const [quote, setQuote] = useState(null);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const index = useMemo(() => buildIndex(), []);

  const results = useMemo(() => {
    const q = normalize(query.trim());
    if (!q || q.length < 2) return [];
    return index
      .filter((r) => {
        const label = normalize(
          r.product[lang] || r.product.pt || r.product.slug
        );
        const brand = normalize(r.brandShort);
        const group = normalize(
          r.groupTitle[lang] || r.groupTitle.pt || ""
        );
        return (
          label.includes(q) ||
          brand.includes(q) ||
          group.includes(q) ||
          normalize(r.product.slug).includes(q)
        );
      })
      .slice(0, 12);
  }, [query, index, lang]);

  // Close dropdown on click outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpenResults(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const handleSelect = (item) => {
    const label = item.product[lang] || item.product.pt || item.product.slug;
    const params = new URLSearchParams({
      produto: label,
      marca: item.brandShort,
    });
    setOpenResults(false);
    navigate(`/?${params.toString()}#contact`);
  };

  const setExample = (term) => {
    setQuery(term);
    setOpenResults(true);
    inputRef.current?.focus();
  };

  return (
    <section
      data-testid="product-search-section"
      className="relative border-b border-slate-200 bg-gradient-to-b from-[#dfecf6] to-[#d6e6f2] py-12 lg:py-16"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900 md:text-4xl lg:text-5xl">
            {s.title}
          </h2>
          <p className="mt-3 text-base leading-relaxed text-slate-600 md:text-lg">
            {s.subtitle}
          </p>
        </div>

        <div
          ref={rootRef}
          className="relative mx-auto mt-8 max-w-3xl"
          data-testid="product-search-root"
        >
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
              strokeWidth={2.2}
            />
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpenResults(true);
              }}
              onFocus={() => setOpenResults(true)}
              placeholder={s.placeholder}
              data-testid="product-search-input"
              className="h-14 w-full rounded-2xl border border-slate-200 bg-white pl-14 pr-12 text-sm text-slate-900 shadow-md placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-4 focus:ring-blue-500/15 md:text-base"
            />
            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  inputRef.current?.focus();
                }}
                data-testid="product-search-clear"
                aria-label="Clear"
                className="absolute right-4 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
              >
                <X className="h-4 w-4" strokeWidth={2.5} />
              </button>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2.5 text-sm text-slate-500 md:text-base">
            <span>{s.hint}</span>
            {s.examples.map((ex) => (
              <button
                key={ex}
                type="button"
                onClick={() => setExample(ex)}
                className="rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm font-semibold text-slate-700 transition-all hover:border-blue-300 hover:text-blue-700 md:text-base"
                data-testid={`product-search-example-${ex}`}
              >
                {ex}
              </button>
            ))}
          </div>

          {/* Dropdown */}
          {openResults && query.trim().length >= 2 && (
            <div
              data-testid="product-search-results"
              className="absolute left-0 right-0 top-full z-20 mt-3 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
            >
              <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-4 py-2">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                  {s.results(results.length)}
                </div>
                <Package className="h-3.5 w-3.5 text-slate-400" strokeWidth={2} />
              </div>

              {results.length === 0 ? (
                <div className="px-5 py-8 text-center text-sm text-slate-500">
                  {s.empty}
                </div>
              ) : (
                <ul className="max-h-[440px] divide-y divide-slate-100 overflow-y-auto">
                  {results.map((r) => {
                    const label = r.product[lang] || r.product.pt;
                    const group = r.groupTitle[lang] || r.groupTitle.pt;
                    return (
                      <li key={r.id}>
                        <button
                          type="button"
                          onClick={() => handleSelect(r)}
                          data-testid={`product-search-result-${r.product.slug}`}
                          className="group flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-slate-50"
                        >
                          <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                            <img
                              src={`${r.imageFolder}/${r.product.slug}.png`}
                              alt={label}
                              loading="lazy"
                              className="h-full w-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span
                                className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white"
                                style={{ background: r.brandColor }}
                              >
                                {r.brandShort}
                              </span>
                              <span className="truncate font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                {group}
                              </span>
                            </div>
                            <div className="mt-1 truncate text-sm font-semibold text-slate-900">
                              {label}
                            </div>
                          </div>
                          <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 transition-colors group-hover:border-blue-400 group-hover:text-blue-700">
                            {psCta}
                            <ArrowUpRight
                              className="h-3 w-3"
                              strokeWidth={2.5}
                            />
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      <QuoteModal
        open={Boolean(quote)}
        onClose={() => setQuote(null)}
        product={quote?.product}
        brand={quote?.brandShort}
        imageFolder={quote?.imageFolder}
        theme={quote?.theme}
      />
    </section>
  );
};

export default ProductSearch;
